import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Wrench, MessageSquare, ArrowRight, CheckCircle } from 'lucide-react';

const Products = () => {
  const { t } = useTranslation();
  const [startingPrices, setStartingPrices] = useState(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const [cmmsRes, chatbotRes] = await Promise.all([
          fetch('/api/subscriptions/cmms/plans'),
          fetch('/api/subscriptions/chatbot/plans'),
        ]);
        if (!cmmsRes.ok || !chatbotRes.ok) return;
        const [cmmsData, chatbotData] = await Promise.all([cmmsRes.json(), chatbotRes.json()]);

        const minMonthly = (data) => {
          const plans = (Array.isArray(data) ? data : (data.content ?? []))
            .filter(p => p.isActive && !p.isTrial)
            .map(p => parseFloat(p.monthlyPrice) || 0)
            .filter(price => price > 0);
          return plans.length ? Math.min(...plans) : null;
        };

        setStartingPrices({
          cmms: minMonthly(cmmsData),
          chatbot: minMonthly(chatbotData),
        });
      } catch {
        // leave null — fall back to translated hardcoded strings
      }
    };
    fetchPrices();
  }, []);

  const formatPrice = (price, fallbackKey) =>
    price != null
      ? `${t('products.startingAt')} $${price}${t('pricing.perMonth')}`
      : t(fallbackKey);

  const products = [
    {
      icon: Wrench,
      title: t('products.cmmsTitle'),
      subtitle: t('products.cmmsSubtitle'),
      description: t('products.cmmsDesc'),
      features: [
        t('products.cmmsFeature1'),
        t('products.cmmsFeature2'),
        t('products.cmmsFeature3'),
        t('products.cmmsFeature4'),
        t('products.cmmsFeature5'),
        t('products.cmmsFeature6'),
      ],
      color: 'primary',
      link: '/cmms',
      plans: formatPrice(startingPrices?.cmms, 'products.cmmsPrice'),
    },
    {
      icon: MessageSquare,
      title: t('products.chatbotTitle'),
      subtitle: t('products.chatbotSubtitle'),
      description: t('products.chatbotDesc'),
      features: [
        t('products.chatbotFeature1'),
        t('products.chatbotFeature2'),
        t('products.chatbotFeature3'),
        t('products.chatbotFeature4'),
        t('products.chatbotFeature5'),
        t('products.chatbotFeature6'),
        t('products.chatbotFeature7'),
      ],
      color: 'secondary',
      link: '/chatbot',
      plans: formatPrice(startingPrices?.chatbot, 'products.chatbotPrice'),
    },
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="section bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('products.title1')} <span className="gradient-text">{t('products.title2')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('products.subtitle')}
          </p>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-2 gap-8"
        >
          {products.map((product, index) => {
            const Icon = product.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group bg-white rounded-2xl border-2 border-gray-200 p-8 hover:border-primary-300 hover:shadow-2xl transition-all duration-300"
              >
                {/* Icon & Title */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl bg-${product.color}-100 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-7 h-7 text-${product.color}-600`} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{product.title}</h3>
                      <p className="text-sm text-gray-500">{product.subtitle}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {product.description}
                </p>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  {product.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing & CTA */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Pricing</div>
                    <div className="text-lg font-bold text-gray-900">{product.plans}</div>
                  </div>
                  <Link
                    to={product.link}
                    className={`btn btn-${product.color} group/btn`}
                  >
                    {t('products.learnMore')}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link to="/pricing" className="btn btn-outline text-lg">
            {t('products.viewPricing')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Products;
